import {Page} from 'Core/models/Page';
import {constant} from 'fp-ts/lib/function';
import {fold as foldOption, fromNullable} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {createAction} from 'Shared/utilities/actionCreator';
import {UserModel} from 'Users/models/UserModel';
import {PageFetchAction} from 'Shared/utilities/sagaApiGenerators';
import {AsyncPage, Async, Empty} from 'Core/models/Async';
import {Agent} from 'Users/models/Agent';

export enum AgentUserActionTypes {
  FETCH_AGENTS = 'agentUser/FETCH_AGENTS',
  FETCH_AGENT = 'agentUser/FETCH_AGENT',
  CHANGE_AGENT = 'agentUser/CHANGE_AGENT',
  CHANGE_AGENTS = 'agentUser/CHANGE_AGENTS',
  NAVIGATE_TO_EDIT = 'agentUser/NAVIGATE_TO_EDIT',
  CHANGE_AGENT_USER_FILTER = 'agentUser/CHANGE_VIP_FILTER',
  CLEAR = 'agentUser/CLEAR',
}

export const EmptyAgentUserFilter: AgentUserFilter = {
  firstName: '',
  userName: '',
  lastName: '',
  agentNumber: '',
  regionCodes: [],
  recruiterCodes: '',
  channel: '',
  encryptedIdentifier: '',
  exclusivityStatusId: '',
};

export type AgentUserFilter = {
  userName: string;
  firstName: string;
  lastName: string;
  agentNumber: string;
  regionCodes: string[];
  channel: string;
  recruiterCodes: string;
  encryptedIdentifier: string;
  exclusivityStatusId: string;
};

export const navigateToEdit = (agent: UserModel, encryptedIdentifier?: string) =>
  createAction(AgentUserActionTypes.NAVIGATE_TO_EDIT, {agent, encryptedIdentifier});
export const fetchAgentUsers = (pageOption?: Page<UserModel>, filter?: AgentUserFilter) => {
  const payload = pipe(
    fromNullable(pageOption),
    foldOption(constant(PageFetchAction.Initial({filter})), page =>
      PageFetchAction.Additional({existingPage: page, filter})
    )
  );

  return createAction(AgentUserActionTypes.FETCH_AGENTS, payload);
};
export const changeAgentUserFilter = (policyFilter: AgentUserFilter) =>
  createAction(AgentUserActionTypes.CHANGE_AGENT_USER_FILTER, policyFilter);
export const changeAgents = (agents: AsyncPage<Page<UserModel>>) =>
  createAction(AgentUserActionTypes.CHANGE_AGENTS, agents);
export const changeAgent = (agent: Async<Agent>) => createAction(AgentUserActionTypes.CHANGE_AGENT, agent);
export const clear = () => createAction(AgentUserActionTypes.CLEAR);

export const AgentUserActions = {
  clear,
  changeAgent,
  changeAgentUserFilter,
  changeAgents,
  fetchAgentUsers,
};

type AgentUserAction =
  | ReturnType<typeof clear>
  | ReturnType<typeof changeAgentUserFilter>
  | ReturnType<typeof changeAgents>
  | ReturnType<typeof changeAgent>;

type AgentUserState = {
  agents: AsyncPage<Page<UserModel>>;
  agent: Async<Agent>;
  agentUserFilter: AgentUserFilter;
};

export const DEFAULT_STATE = {
  agent: new Empty(),
  agentUserFilter: EmptyAgentUserFilter,
  agents: new Empty(),
};

export default (state: AgentUserState = DEFAULT_STATE, action: AgentUserAction): AgentUserState => {
  switch (action.type) {
    case AgentUserActionTypes.CHANGE_AGENT:
      return {
        ...state,
        agent: action.payload,
      };
    case AgentUserActionTypes.CHANGE_AGENTS:
      return {
        ...state,
        agents: action.payload,
      };
    case AgentUserActionTypes.CHANGE_AGENT_USER_FILTER:
      return {
        ...state,
        agentUserFilter: action.payload,
      };
    case AgentUserActionTypes.CLEAR:
      return DEFAULT_STATE;
    default:
      return state;
  }
};
